/**
 * EvolutionConnectionDialog
 *
 * Modal que cria uma instancia Evolution end-to-end:
 *  1. Recebe nome amigavel (ex: "Atendimento 02")
 *  2. Slugifica pra instance_name (ex: "prime-atendimento-02")
 *  3. Chama evolution-instance-manage action='create' -> recebe QR
 *  4. Persiste whatsapp_connections no motor correto antes de exibir o QR
 *  5. Renderiza QR base64
 *  6. Faz polling de connectionState a cada 3s (timeout 90s)
 *  7. Quando state='open': busca ownerJid e completa o telefone real
 *  8. Aplica funcao (atendimento/comercial) se selecionada
 *  9. Fecha e atualiza lista
 *
 * Em qualquer erro, deixa a instancia criada no Evolution pra reuso
 * (logout/delete via outros caminhos). Fechar o modal preserva o cadastro e a sessão.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContentMaxPriority,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, QrCode, Smartphone, CheckCircle2, RefreshCw, X, AlertTriangle, Copy, Check, ExternalLink, Send } from 'lucide-react';

import WhatsAppQrInstructions from './WhatsAppQrInstructions';
import { formatConnectionPhone } from '@/lib/whatsappErrorMessages';

type Phase = 'form' | 'creating' | 'qr' | 'connecting' | 'validating' | 'success' | 'timeout' | 'error' | 'share';
type RoleValue = 'nenhuma' | 'atendimento' | 'comercial';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess?: () => void;
  /**
   * Modo reconectar: se passado, pula a fase de form e vai direto pra QR
   * desta instância existente. Útil pra "Reconectar" instância que perdeu sessão.
   */
  reconnectInstanceName?: string;
}

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90_000;
const QR_REFRESH_MS = 18_000;

export default function EvolutionConnectionDialog({ open, onOpenChange, onSuccess, reconnectInstanceName }: Props) {
  const [phase, setPhase] = useState<Phase>('form');
  const [expectedPhone, setExpectedPhone] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [funcao, setFuncao] = useState<RoleValue>('nenhuma');
  const [instanceName, setInstanceName] = useState('');
  const [qrBase64, setQrBase64] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollStartRef = useRef<number>(0);
  const lastQrRef = useRef<number>(0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runRef = useRef(0);
  const expectedPhoneRef = useRef<string | null>(null);
  const connectionIdRef = useRef<string | null>(null);

  const resetState = () => {
    runRef.current++;
    setPhase('form');
    setNome('');
    setExpectedPhone(null);
    expectedPhoneRef.current = null;
    setFuncao('nenhuma');
    setInstanceName('');
    connectionIdRef.current = null;
    setQrBase64(null);
    setErrorMsg(null);
    setShareUrl(null);
    setSharing(false);
    setCopied(false);
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // Gera o link público /conectar/<token> pra ESTA instância recém-criada.
  // Persiste a conexão no banco AGORA (antes era só após o scan presencial —
  // por isso conexões "criadas mas não escaneadas" se perdiam) e gera o token
  // de compartilhamento. O dono do número abre o link no celular dele, vê o
  // QR e escaneia sozinho — sem login no CRM. Para o polling do modal: a
  // finalização (nome + telefone real) vem pelo evolution-qr-public.
  async function handleShareLink() {
    if (!instanceName || sharing) return;
    const run = ++runRef.current;
    setSharing(true);
    setErrorMsg(null);
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    try {
      // Criação e reconexão usam o cadastro já existente. Compartilhar um
      // QR nunca deve inserir outra linha ou trocar o motor do cadastro.
      const conn = connectionIdRef.current ? { id: connectionIdRef.current } : null;
      if (!conn) throw new Error('Não foi possível identificar a linha. Feche e abra a conexão novamente.');

      // 1b. Função (role) opcional — mesmo comportamento do finalizeOnConnect:
      //     se já há ativa nessa função, esta entra como reserva (banned).
      if (funcao !== 'nenhuma' && conn?.id) {
        const { data: existing } = await supabase
          .from('whatsapp_role_connections')
          .select('connection_id')
          .eq('role', funcao)
          .eq('status', 'active')
          .maybeSingle();
        await supabase
          .from('whatsapp_role_connections')
          .insert({ role: funcao, connection_id: conn.id, status: existing ? 'banned' : 'active' });
      }

      // 2. Gera o token de link compartilhável (vale 30 min).
      const { data: tk, error: tkErr } = await supabase.functions.invoke('evolution-regenerate-share-token', {
        body: { connection_id: conn.id },
      });
      if (run !== runRef.current) return;
      if (tkErr) throw tkErr;
      if (!tk?.ok || !tk?.token) throw new Error(tk?.error || 'Falha ao gerar o link');

      setShareUrl(`${window.location.origin}/conectar/${tk.token}`);
      setPhase('share');
      // A confirmação fica aberta para copiar o link; Concluir atualiza a lista.
    } catch (err: unknown) {
      if (run !== runRef.current) return;
      setErrorMsg(err instanceof Error ? err.message : 'Não foi possível concluir. Tente novamente.');
      setPhase('error');
    } finally {
      if (run === runRef.current) setSharing(false);
    }
  }

  const mensagemPronta = shareUrl
    ? `Olá! Pra ativar seu WhatsApp no CRM Prime, abra o link abaixo no computador ou em outro aparelho. No celular da linha, abra WhatsApp → Aparelhos conectados → Conectar um aparelho e escaneie o QR da tela.\n\n${shareUrl}\n\nComo funciona:\n• O link expira em 30 minutos.\n• Confira o número indicado na página antes de escanear.\n• Pode abrir quando puder, dentro dos 30 min.`
    : '';

  function handleCopyMensagem() {
    if (!mensagemPronta) return;
    navigator.clipboard.writeText(mensagemPronta).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => toast.error('Não consegui copiar — selecione o texto manualmente'),
    );
  }

  // Carrega o cadastro real antes de oferecer QR ou compartilhamento.
  useEffect(() => {
    let cancelled = false;
    const generation = runRef;
    if (!open) resetState();
    else if (reconnectInstanceName) {
      setPhase('creating');
      setInstanceName('');
      void (async () => {
        const { data, error } = await supabase.from('whatsapp_connections')
          .select('id,nome,telefone')
          .eq('provider_instance_id', reconnectInstanceName)
          .eq('ativo', true).is('deleted_at', null).limit(2);
        if (cancelled) return;
        if (error || data?.length !== 1) {
          setErrorMsg('Não foi possível identificar uma única linha para reconectar. Peça ao responsável pelas conexões para conferir o cadastro.');
          setPhase('error');
          return;
        }
        setInstanceName(reconnectInstanceName);
        connectionIdRef.current = data[0].id;
        expectedPhoneRef.current = data[0].telefone || null;
        setNome(data[0].nome || 'WhatsApp');
        setExpectedPhone(data[0].telefone || null);
        await doRefreshQr(reconnectInstanceName);
      })().catch(() => {
        if (cancelled) return;
        setErrorMsg('Não foi possível carregar a linha. Feche e abra a conexão novamente.');
        setPhase('error');
      });
    }
    return () => { cancelled = true; generation.current++; if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reconnectInstanceName]);

  async function handleCreate() {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) {
      toast.error('Informe um nome');
      return;
    }
    // O nome da INSTANCIA na Evolution e gerado pelo backend (empresarial-prime-NN).
    // O que o usuario digita e so o rotulo (nome) da conexao no CRM.
    setPhase('creating');
    setErrorMsg(null);
    const run = ++runRef.current;

    try {
      const { data, error } = await supabase.functions.invoke('evolution-instance-manage', {
        body: { action: 'create', connection_name: nomeLimpo },
      });
      if (run !== runRef.current) return;
      if (error) throw error;
      if (data?.ok === false) {
        const status = data.status || '?';
        const msg = data.data?.response?.message?.[0] || data.data?.message || data.error || 'Falha desconhecida';
        throw new Error(`${msg} (HTTP ${status})`);
      }
      const fullInstanceName = data?.data?.instance_name || '';
      if (!fullInstanceName) {
        throw new Error('Backend nao retornou o nome da instancia. Tente de novo.');
      }
      setInstanceName(fullInstanceName);
      connectionIdRef.current = data?.data?.connection_id || null;

      // CRÍTICO: aplica settings ANTES do QR ser escaneado. syncFullHistory=true
      // faz o Baileys baixar o histórico do celular na primeira conexão. Se setar
      // depois do QR (já conectado), perde-se o evento messaging-history.set e
      // o histórico fica perdido pra sempre. Só funciona ANTES do QR.
      try {
        await supabase.functions.invoke('evolution-instance-manage', {
          body: {
            action: 'updateSettings',
            instance_name: fullInstanceName,
            settings: {
              syncFullHistory: true,
              groupsIgnore: true,
              readMessages: false,
              readStatus: false,
              rejectCall: false,
              alwaysOnline: false,
            },
          },
        });
      } catch (settingsErr) {
        // Não bloqueia o fluxo do QR — só loga. Mas no pior caso o histórico
        // não vem (o webhook continua funcionando normalmente pra mensagens novas).
        console.warn('[evolution-dialog] updateSettings falhou (segue mesmo assim):', settingsErr);
      }

      if (run !== runRef.current) return;
      const b64 = data?.data?.qrcode?.base64 || data?.data?.base64 || '';
      setQrBase64(b64 || null);
      setPhase(b64 ? 'qr' : 'connecting');
      pollStartRef.current = Date.now();
      lastQrRef.current = Date.now();
      schedulePoll(fullInstanceName);
    } catch (err: unknown) {
      if (run !== runRef.current) return;
      setErrorMsg(err instanceof Error ? err.message : 'Não foi possível concluir. Tente novamente.');
      setPhase('error');
    }
  }

  function schedulePoll(instance: string) {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(() => pollState(instance), POLL_INTERVAL_MS);
  }

  async function pollState(instance: string) {
    const run = runRef.current;
    try {
      const elapsed = Date.now() - pollStartRef.current;
      if (elapsed > POLL_TIMEOUT_MS) {
        setPhase('timeout');
        return;
      }

      const { data, error } = await supabase.functions.invoke('evolution-instance-manage', {
        body: { action: 'connectionState', instance_name: instance },
      });
      if (run !== runRef.current) return;
      if (error || !data || data.ok === false) throw new Error('Não conseguimos consultar a conexão. Confira a internet e tente novamente.');
      const state = data?.data?.instance?.state || data?.data?.state || null;

      if (state === 'open') {
        setPhase('validating');
        if (reconnectInstanceName) {
          await finalizeReconnect(instance);
        } else {
          await finalizeOnConnect(instance);
        }
        return;
      }

      // QR do WhatsApp expira em ~20s — renova a imagem sozinho enquanto espera o scan,
      // senao o usuario escaneia um QR morto e nao conecta. (18/06)
      if (Date.now() - lastQrRef.current > QR_REFRESH_MS) {
        const { data: q, error: qrError } = await supabase.functions
          .invoke('evolution-instance-manage', { body: { action: 'qr', instance_name: instance } })
          .catch(() => ({ data: null, error: new Error('QR indisponível') }));
        if (run !== runRef.current) return;
        if (qrError || !q || q.ok === false) throw new Error('Não foi possível atualizar o QR. Tente novamente.');
        const fresh = q?.data?.base64 || '';
        if (fresh) {
          setQrBase64(fresh);
          setPhase('qr');
          lastQrRef.current = Date.now();
        }
      }

      // Estados intermediarios: continua poll
      schedulePoll(instance);
    } catch (e) {
      if (run !== runRef.current) return;
      setQrBase64(null);
      setErrorMsg(e instanceof Error ? e.message : 'Não foi possível confirmar a conexão. Tente novamente.');
      setPhase('error');
    }
  }

  async function refreshQr() {
    if (!instanceName) return;
    await doRefreshQr(instanceName);
  }

  async function doRefreshQr(instance: string) {
    const run = ++runRef.current;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setPhase('creating');
    setErrorMsg(null);
    setQrBase64(null);
    try {
      // 1) Tenta pegar QR de uma instância EXISTENTE
      const { data, error } = await supabase.functions.invoke('evolution-instance-manage', {
        body: { action: 'qr', instance_name: instance },
      });
      if (run !== runRef.current) return;
      if (error || !data) throw new Error(error?.message || 'Não foi possível consultar a instância. Tente novamente.');
      if (data?.ok === false) throw new Error(data.error || 'Não foi possível consultar a instância. Tente novamente.');
      const b64 = data?.data?.base64 || '';

      if (!b64) {
        setPhase('connecting');
        pollStartRef.current = Date.now();
        lastQrRef.current = 0;
        schedulePoll(instance);
        return;
      }

      setQrBase64(b64);
      setPhase('qr');
      pollStartRef.current = Date.now();
      lastQrRef.current = Date.now();
      schedulePoll(instance);
    } catch (err: unknown) {
      if (run !== runRef.current) return;
      setErrorMsg(err instanceof Error ? err.message : 'Não foi possível concluir. Tente novamente.');
      setPhase('error');
    }
  }

  /**
   * Reconectar instância existente: não cria nova row em whatsapp_connections,
   * apenas confirma que voltou online.
   */
  async function finalizeReconnect(instance: string) {
    const run = runRef.current;
    const { data, error } = await supabase.functions.invoke('evolution-instance-manage', {
      body: { action: 'fetchProfile', instance_name: instance },
    });
    if (run !== runRef.current) return;
    const phone = String(data?.data?.ownerJid || '').split('@')[0].replace(/:\d+$/, '').replace(/\D/g, '');
    const expected = String(expectedPhoneRef.current || '').replace(/\D/g, '');
    if (error || data?.ok === false || phone.length < 10) {
      setErrorMsg('Ainda não conseguimos confirmar o número conectado. Tente verificar novamente.');
      setPhase('error');
      return;
    }
    if (expected.length >= 8 && phone.slice(-8) !== expected.slice(-8)) {
      setErrorMsg(`O WhatsApp conectado é ${formatConnectionPhone(phone)}, mas esta linha espera ${formatConnectionPhone(expected)}. Peça ao responsável pelas conexões para corrigir o pareamento.`);
      setPhase('error');
      return;
    }
    setExpectedPhone(phone);
    setQrBase64(null);
    setPhase('success');
    toast.success(`WhatsApp ${formatConnectionPhone(phone)} conectado!`);
    try { await supabase.functions.invoke('whatsapp-health-watchdog'); }
    catch { /* O próximo ciclo atualiza a saúde. */ }
    // Mantém a confirmação visível; o usuário conclui após conferir o número.
  }

  async function finalizeOnConnect(instance: string) {
    const run = runRef.current;
    try {
      // Pega a própria instância no motor correto pra extrair o telefone real.
      const { data: fetched } = await supabase.functions.invoke('evolution-instance-manage', {
        body: { action: 'fetchProfile', instance_name: instance },
      });
      if (run !== runRef.current) return;
      const mine = fetched?.data || null;
      const ownerJid = mine?.ownerJid || '';
      const telefone = ownerJid.split('@')[0]?.replace(/:\d+$/, '') || '';

      if (telefone.replace(/\D/g, '').length < 10) {
        throw new Error('O WhatsApp ainda não informou o número conectado. Tente verificar novamente.');
      }

      const persistedConnectionId = connectionIdRef.current;
      const created: { id: string } | null = persistedConnectionId ? { id: persistedConnectionId } : null;
      if (persistedConnectionId) {
        const { error: updErr } = await supabase
          .from('whatsapp_connections')
          .update({ nome, telefone, ativo: true, updated_at: new Date().toISOString() })
          .eq('id', persistedConnectionId);
        if (updErr) throw updErr;
      } else {
        throw new Error('Não foi possível identificar o cadastro da linha. Feche e abra a conexão novamente.');
      }

      // Funcao (role) — opcional
      if (funcao !== 'nenhuma' && created?.id) {
        const { data: existing } = await supabase
          .from('whatsapp_role_connections')
          .select('connection_id')
          .eq('role', funcao)
          .eq('status', 'active')
          .maybeSingle();
        // Se ja existe outra active, cadastra esta como banned (reserva) — comportamento
        // padrao do ConnectionsManager: nao promove automaticamente.
        const status = existing ? 'banned' : 'active';
        await supabase
          .from('whatsapp_role_connections')
          .insert({ role: funcao, connection_id: created.id, status });
      }

      if (run !== runRef.current) return;
      setExpectedPhone(telefone);
      setQrBase64(null);
      setPhase('success');
      toast.success(`WhatsApp ${formatConnectionPhone(telefone)} conectado!`);
    } catch (err: unknown) {
      if (run !== runRef.current) return;
      setErrorMsg(err instanceof Error ? err.message : 'Não foi possível concluir. Tente novamente.');
      setPhase('error');
    }
  }

  async function handleCancel() {
    runRef.current++;
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    // Fechar a tela não descarta um cadastro nem uma sessão recém-pareada.
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <DialogContentMaxPriority className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5 text-success" />
            {reconnectInstanceName ? 'Reconectar WhatsApp' : 'Conectar WhatsApp'}
          </DialogTitle>
          <DialogDescription>
            Tenha o celular da linha em mãos. O CRM vai mostrar o QR e confirmar o número conectado.
          </DialogDescription>
        </DialogHeader>

        {phase === 'form' && (
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs">Nome amigável *</Label>
              <Input
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Atendimento 02"
                className="h-9"
                autoFocus
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Este é só o rótulo no CRM. O identificador no Evolution é gerado automaticamente
                (<code className="font-mono">empresarial-prime-NN</code>).
              </p>
            </div>
            <div>
              <Label className="text-xs">Função (opcional)</Label>
              <Select value={funcao} onValueChange={(v) => setFuncao(v as RoleValue)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nenhuma">Nenhuma</SelectItem>
                  <SelectItem value="atendimento">Atendimento</SelectItem>
                  <SelectItem value="comercial">Comercial</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1 leading-tight">
                Se já existe conexão ativa com essa função, esta entra como reserva (sem promover automaticamente).
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
                <X className="h-3.5 w-3.5 mr-1" /> Cancelar
              </Button>
              <Button size="sm" onClick={handleCreate} disabled={!nome.trim()}>
                <QrCode className="h-3.5 w-3.5 mr-1" /> Criar e gerar QR
              </Button>
            </div>
          </div>
        )}

        {phase === 'creating' && (
          <div className="py-8 flex flex-col items-center gap-3 text-sm">
            <Loader2 className="h-8 w-8 animate-spin text-success" />
            <p>Preparando o QR da sua linha…</p>
          </div>
        )}

        {phase === 'qr' && qrBase64 && (
          <div className="space-y-3 text-center">
            <WhatsAppQrInstructions telefone={expectedPhone} />
            <div className="flex justify-center bg-white rounded-md p-4 border">
              <img
                src={`data:image/png;base64,${qrBase64.replace(/^data:image\/png;base64,/, '')}`}
                alt="QR Code Evolution"
                className="w-56 h-56"
              />
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground flex items-center justify-center gap-1">
                <Smartphone className="h-3.5 w-3.5" /> Leia o QR com o WhatsApp da linha
              </p>
              <p>WhatsApp → Aparelhos conectados → Conectar um aparelho</p>
              <p className="text-success flex items-center justify-center gap-1 pt-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Aguardando escaneamento…
              </p>
            </div>

            {/* Caminho remoto: o número é de outra pessoa → manda o link pra ela conectar sozinha */}
            <div className="pt-2 mt-1 border-t">
              <p className="text-[11px] text-muted-foreground mb-2 leading-snug">
                O número é de outra pessoa (supervisora, consultor)?
                <br />Copie o link para ela abrir no computador e escanear com o celular da linha:
              </p>
              <Button
                size="sm"
                className="w-full gap-1.5 bg-success hover:bg-success text-white"
                onClick={handleShareLink}
                disabled={sharing}
              >
                {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Preparar link para compartilhar
              </Button>
            </div>

            <div className="flex justify-between pt-1">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="h-3.5 w-3.5 mr-1" /> Cancelar
              </Button>
              <Button variant="outline" size="sm" onClick={refreshQr}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Gerar novo QR
              </Button>
            </div>
          </div>
        )}

        {phase === 'share' && shareUrl && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-success/10 border border-success/30 text-sm">
              <p className="font-semibold text-success dark:text-success mb-1 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Link pronto — aguardando pareamento
              </p>
              <p className="text-xs text-muted-foreground">
                A conexão <strong>{nome}</strong> já está salva. Manda o link abaixo pro dono do número —
                ele abre no computador e escaneia com o celular da linha, <strong>sem login no CRM</strong>. O link vale 30 min.
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Mensagem pronta pra enviar</Label>
              <textarea
                readOnly
                value={mensagemPronta}
                rows={7}
                className="w-full px-3 py-2 text-xs rounded border bg-muted/30 cursor-text leading-relaxed font-mono"
                onClick={(e) => (e.target as HTMLTextAreaElement).select()}
              />
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 gap-1.5 bg-success hover:bg-success text-white" onClick={handleCopyMensagem}>
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copiado — cola no WhatsApp' : 'Copiar mensagem'}
                </Button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(mensagemPronta)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-1 h-9 px-3 text-xs rounded-md border bg-background hover:bg-accent"
                  title="Abrir no WhatsApp pra escolher pra quem mandar"
                >
                  <Send className="h-3.5 w-3.5" /> WhatsApp
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 flex-1"
                onClick={() => window.open(shareUrl, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="h-3.5 w-3.5" /> Abrir aqui pra escanear agora
              </Button>
              <Button size="sm" className="flex-1" onClick={() => { onSuccess?.(); onOpenChange(false); }}>
                Fechar
              </Button>
            </div>
          </div>
        )}

        {(phase === 'connecting' || phase === 'validating') && (
          <div className="py-8 flex flex-col items-center gap-3 text-sm">
            <Loader2 className="h-8 w-8 animate-spin text-success" />
            <p>{phase === 'validating' ? 'Validando o número conectado…' : 'Preparando o QR da sua linha…'}</p>
          </div>
        )}

        {phase === 'success' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <p role="status" className="text-sm font-medium">WhatsApp conectado!</p>
            <p className="font-semibold">{formatConnectionPhone(expectedPhone)}</p>
            <p className="text-sm text-muted-foreground">{nome}</p>
            <Button onClick={() => { onSuccess?.(); onOpenChange(false); }}>Concluir</Button>
          </div>
        )}

        {phase === 'timeout' && (
          <div className="py-6 flex flex-col items-center gap-3 text-sm">
            <AlertTriangle className="h-8 w-8 text-primary" />
            <p className="text-center">A conexão ainda não foi confirmada. Confira a internet do celular e tente novamente para obter o QR atual.</p>
            <div className="flex gap-2 pt-1">
              <Button variant="ghost" size="sm" onClick={handleCancel}>Cancelar</Button>
              <Button size="sm" onClick={refreshQr}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Gerar novo QR
              </Button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="py-6 flex flex-col items-center gap-3 text-sm">
            <AlertTriangle className="h-8 w-8 text-destructive" />
            <p className="text-center text-destructive">{errorMsg}</p>
            <div className="flex gap-2 pt-1">
              {instanceName ? <Button size="sm" onClick={refreshQr}>Tentar novamente</Button> : !reconnectInstanceName && <Button variant="ghost" size="sm" onClick={() => setPhase('form')}>Voltar</Button>}
              <Button variant="outline" size="sm" onClick={handleCancel}>Fechar</Button>
            </div>
          </div>
        )}
      </DialogContentMaxPriority>
    </Dialog>
  );
}
